// src/components/HomePage.js

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import heroIllustration from '../assets/hero-illustration.png';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CelebrationIcon from '@mui/icons-material/Celebration';
import ShareIcon from '@mui/icons-material/Share';

// ─── useInView hook ───────────────────────────────────────────────────────────
function useInView(threshold = 0.15) {
  const [inView, setInView] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

// ─── Animated section wrapper ─────────────────────────────────────────────────
function AnimatedSection({ children, delay = 0, sx = {} }) {
  const { ref, inView } = useInView();
  return (
    <Box
      ref={ref}
      sx={{
        opacity: inView ? 1 : 0,
        animation: inView ? `fadeSlideUp 0.65s cubic-bezier(0.4,0,0.2,1) ${delay}s both` : 'none',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

// ─── Hero floating card components ───────────────────────────────────────────
function HeroCard({ rotate, delay, zIndex, children }) {
  return (
    <Paper
      elevation={8}
      sx={{
        position: 'absolute',
        width: { xs: 220, sm: 260 },
        p: 2,
        borderRadius: 3,
        background: '#FFFFFF',
        border: '1px solid #EDE8DF',
        boxShadow: '0 20px 60px rgba(26,20,16,0.14), 0 4px 12px rgba(26,20,16,0.08)',
        transform: `rotate(${rotate}deg)`,
        zIndex,
        animation: `float 4s ease-in-out ${delay}s infinite`,
        animationFillMode: 'both',
        transition: 'none',
        '&:hover': { transform: `rotate(${rotate}deg) translateY(-4px)` },
      }}
    >
      {children}
    </Paper>
  );
}

// ─── Feature icon container ───────────────────────────────────────────────────
function FeatureIcon({ icon: Icon, color, bg }) {
  return (
    <Box
      sx={{
        width: 52,
        height: 52,
        borderRadius: '14px',
        background: bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 2,
        transition: 'all 0.2s',
        '&:hover': { animation: 'pulseGlow 1.5s ease-out' },
      }}
    >
      <Icon sx={{ color, fontSize: 26 }} />
    </Box>
  );
}

// ─── Mini expense dashboard (CSS-rendered, not an image) ──────────────────────
function MiniExpenseDashboard() {
  const rows = [
    { name: 'Emma', paid: '€45.00', owes: null, color: '#2D6A4F' },
    { name: 'Tom', paid: '€15.00', owes: '€15.00', color: '#E11D48' },
    { name: 'Sara', paid: '€24.50', owes: '€5.50', color: '#E11D48' },
    { name: 'You', paid: '€0.00', owes: '€20.50', color: '#E11D48' },
  ];
  return (
    <Box
      sx={{
        background: '#FFFFFF',
        borderRadius: 3,
        border: '1px solid #EDE8DF',
        boxShadow: '0 8px 32px rgba(26,20,16,0.10)',
        p: 3,
        maxWidth: 340,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1A1410', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
          🎄 Christmas Dinner Costs
        </Typography>
        <Chip
          label="€84.50 total"
          size="small"
          sx={{ background: 'rgba(45,106,79,0.1)', color: '#2D6A4F', fontWeight: 700, fontSize: '0.7rem' }}
        />
      </Box>
      {rows.map((row) => (
        <Box
          key={row.name}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1,
            borderBottom: '1px solid #F7F2EA',
            '&:last-child': { borderBottom: 'none' },
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1A1410', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
            {row.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#5C4F3D', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              paid {row.paid}
            </Typography>
            {row.owes && (
              <Chip
                label={`owes ${row.owes}`}
                size="small"
                sx={{ background: 'rgba(225,29,72,0.08)', color: '#E11D48', fontWeight: 700, fontSize: '0.65rem', height: 20 }}
              />
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ─── Comparison table data ────────────────────────────────────────────────────
const comparisonRows = [
  { feature: 'Expense tracking dashboard', listlink: true, elfster: false, giftster: false },
  { feature: 'Sinterklaas support', listlink: true, elfster: false, giftster: false },
  { feature: 'Grocery / packing / to-do lists', listlink: true, elfster: false, giftster: false },
  { feature: '100% free (no hidden limits)', listlink: true, elfster: 'partial', giftster: 'partial' },
];

function ComparisonCell({ value }) {
  if (value === true) return <CheckIcon sx={{ color: '#2D6A4F', fontSize: 22 }} />;
  if (value === false) return <CloseIcon sx={{ color: '#C9C0B3', fontSize: 22 }} />;
  return <RemoveIcon sx={{ color: '#C9C0B3', fontSize: 22 }} />;
}

// ─── Structured data ──────────────────────────────────────────────────────────
const structuredData = {
  '@context': 'http://schema.org',
  '@type': 'WebApplication',
  name: 'ListLink',
  url: 'https://list-link.com',
  description:
    'Free gift list and Secret Santa app with expense tracking. Create wish lists, organize Sinterklaas and Secret Santa events, track shared expenses, and manage grocery and packing lists.',
  applicationCategory: 'Productivity',
  operatingSystem: 'All',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
};

// ─── Main component ───────────────────────────────────────────────────────────
function HomePage() {
  return (
    <Box sx={{ background: '#FDFAF5', overflowX: 'hidden' }}>
      <Helmet>
        <title>ListLink — Free Gift Lists, Secret Santa & Expense Tracker</title>
        <meta
          name="description"
          content="Create wish lists, organize Secret Santa and Sinterklaas events, track shared expenses, and manage grocery lists. The only free app that does it all."
        />
        <meta
          name="keywords"
          content="gift list app, secret santa organizer, sinterklaas app, expense tracking, shared lists, grocery list, wish list, free gift app"
        />
        <meta property="og:title" content="ListLink — Free Gift Lists, Secret Santa & Expense Tracker" />
        <meta property="og:description" content="Create wish lists, organize Secret Santa and Sinterklaas events, track shared expenses, and manage grocery lists. Free." />
        <meta property="og:url" content="https://list-link.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://list-link.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ListLink — Free Gift Lists, Secret Santa & Expense Tracker" />
        <meta name="twitter:description" content="Create wish lists, organize Secret Santa and Sinterklaas events, track shared expenses, and manage grocery lists. Free." />
        <link rel="canonical" href="https://list-link.com/" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      {/* ── SECTION 1: HERO ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 'auto', md: '88vh' },
          display: 'flex',
          alignItems: 'center',
          background: `
            radial-gradient(at 20% 50%, rgba(253,235,208,0.9) 0%, transparent 55%),
            radial-gradient(at 80% 10%, rgba(254,243,199,0.7) 0%, transparent 50%),
            radial-gradient(at 50% 90%, rgba(243,232,255,0.3) 0%, transparent 55%),
            #FDFAF5
          `,
          backgroundImage: `
            radial-gradient(at 20% 50%, rgba(253,235,208,0.9) 0%, transparent 55%),
            radial-gradient(at 80% 10%, rgba(254,243,199,0.7) 0%, transparent 50%),
            radial-gradient(at 50% 90%, rgba(243,232,255,0.3) 0%, transparent 55%),
            url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23E8891A' fill-opacity='0.04'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")
          `,
          backgroundSize: 'auto, auto, auto, 40px 40px',
          py: { xs: 10, md: 12 },
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            {/* Text content */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  opacity: 0,
                  animation: 'fadeSlideUp 0.65s cubic-bezier(0.4,0,0.2,1) 0.1s both',
                }}
              >
                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    fontSize: { xs: '2.6rem', sm: '3.2rem', md: '3.8rem' },
                    mb: 1,
                    color: '#1A1410',
                  }}
                >
                  Gift Lists That
                  <br />
                  <Box
                    component="span"
                    sx={{
                      fontFamily: '"Fraunces", serif',
                      fontStyle: 'italic',
                      color: '#E8891A',
                    }}
                  >
                    Actually Work.
                  </Box>
                </Typography>
              </Box>

              <Box
                sx={{
                  opacity: 0,
                  animation: 'fadeSlideUp 0.65s cubic-bezier(0.4,0,0.2,1) 0.2s both',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: '1rem', md: '1.15rem' },
                    color: '#5C4F3D',
                    mb: 4,
                    mt: 2.5,
                    lineHeight: 1.75,
                    maxWidth: 520,
                  }}
                >
                  Create wish lists, organize Secret Santa and Sinterklaas exchanges, track
                  shared expenses, and collaborate on grocery or packing lists — all in one
                  free app.
                </Typography>
              </Box>

              <Box
                sx={{
                  opacity: 0,
                  animation: 'fadeSlideUp 0.65s cubic-bezier(0.4,0,0.2,1) 0.3s both',
                  display: 'flex',
                  gap: 2,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/register"
                  sx={{ px: 4, py: 1.75, fontSize: '1rem' }}
                >
                  Start For Free →
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/learn-more"
                  sx={{ px: 3 }}
                >
                  See How It Works
                </Button>
              </Box>

              <Box
                sx={{
                  opacity: 0,
                  animation: 'fadeSlideUp 0.65s cubic-bezier(0.4,0,0.2,1) 0.4s both',
                  display: 'flex',
                  gap: 3,
                  flexWrap: 'wrap',
                }}
              >
                {['Free forever', 'No credit card', 'Works on any device'].map((text) => (
                  <Box
                    key={text}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                  >
                    <CheckCircleIcon sx={{ color: '#2D6A4F', fontSize: 16 }} />
                    <Typography
                      sx={{
                        fontSize: '0.82rem',
                        fontWeight: 600,
                        color: '#5C4F3D',
                        fontFamily: '"Plus Jakarta Sans", sans-serif',
                      }}
                    >
                      {text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Floating card stack */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  height: { xs: 280, sm: 340, md: 400 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  animation: 'fadeSlideUp 0.65s cubic-bezier(0.4,0,0.2,1) 0.25s both',
                }}
              >
                {/* Background illustration — sits behind the floating cards */}
                <Box
                  component="img"
                  src={heroIllustration}
                  alt=""
                  aria-hidden="true"
                  sx={{
                    position: 'absolute',
                    width: { xs: '90%', sm: '85%', md: '95%' },
                    maxWidth: 480,
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 0,
                    opacity: 0.9,
                    pointerEvents: 'none',
                    userSelect: 'none',
                  }}
                />

                {/* Card 3: Secret Santa event — back */}
                <HeroCard rotate={6} delay={2.6} zIndex={1}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#1A1410', fontFamily: '"Plus Jakarta Sans", sans-serif', mb: 1 }}>
                    🎅 Secret Santa
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: '#5C4F3D', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                    6 participants
                  </Typography>
                  <Box sx={{ mt: 1, height: 4, borderRadius: 100, background: 'linear-gradient(90deg, #E8891A, #F5A94E)', width: '70%' }} />
                  <Typography sx={{ fontSize: '0.65rem', color: '#8A7968', mt: 0.75, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                    Draw in 3 days
                  </Typography>
                </HeroCard>

                {/* Card 2: Expense summary — middle */}
                <HeroCard rotate={-4} delay={1.3} zIndex={2}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#1A1410', fontFamily: '"Plus Jakarta Sans", sans-serif', mb: 1.5 }}>
                    💸 Group Expenses
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#5C4F3D', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Total spent</Typography>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1A1410', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>€84.50</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#5C4F3D', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>You're owed</Typography>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#2D6A4F', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>€22.00</Typography>
                  </Box>
                </HeroCard>

                {/* Card 1: Gift list — front */}
                <HeroCard rotate={-2} delay={0} zIndex={3}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#1A1410', fontFamily: '"Plus Jakarta Sans", sans-serif', mb: 1.5 }}>
                    🎁 Emma's Wish List
                  </Typography>
                  {[
                    { item: 'Wireless headphones', bought: true },
                    { item: 'Cozy blanket', bought: false },
                    { item: 'Recipe book', bought: false },
                  ].map(({ item, bought }) => (
                    <Box
                      key={item}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.75,
                        opacity: bought ? 0.5 : 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          border: bought ? 'none' : '1.5px solid #EDE8DF',
                          background: bought ? '#2D6A4F' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {bought && <CheckIcon sx={{ fontSize: 10, color: '#fff' }} />}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '0.72rem',
                          color: '#1A1410',
                          fontFamily: '"Plus Jakarta Sans", sans-serif',
                          textDecoration: bought ? 'line-through' : 'none',
                        }}
                      >
                        {item}
                      </Typography>
                      {bought && (
                        <Chip
                          label="Secretly ✓"
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: '0.58rem',
                            fontWeight: 700,
                            background: 'rgba(45,106,79,0.1)',
                            color: '#2D6A4F',
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </HeroCard>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── SECTION 2: FEATURE SHOWCASE ──────────────────────────────────────── */}
      <Box sx={{ py: { xs: 10, md: 14 }, background: '#FDFAF5' }}>
        <Container maxWidth="lg">
          <AnimatedSection>
            <Typography
              variant="overline"
              sx={{ color: '#E8891A', display: 'block', mb: 1, textAlign: 'center' }}
            >
              Features
            </Typography>
            <Typography
              variant="h2"
              sx={{ textAlign: 'center', mb: 1.5, fontSize: { xs: '2rem', md: '2.6rem' } }}
            >
              Everything Your Gift Season Needs
            </Typography>
            <Typography
              sx={{
                textAlign: 'center',
                color: '#5C4F3D',
                fontSize: '1.05rem',
                mb: 8,
                maxWidth: 480,
                mx: 'auto',
              }}
            >
              One app. Zero chaos. Completely free.
            </Typography>
          </AnimatedSection>

          {/* Feature 1: Full-width — Expense Tracking */}
          <AnimatedSection delay={0.05}>
            <Paper
              sx={{
                p: { xs: 4, md: 6 },
                mb: 3,
                borderRadius: '24px',
                border: '1px solid #EDE8DF',
                boxShadow: '0 4px 24px rgba(26,20,16,0.07)',
                overflow: 'hidden',
                '&:hover': { transform: 'none' },
              }}
            >
              <Grid container spacing={5} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Chip
                    label="Unique — No Competitor Offers This"
                    size="small"
                    sx={{
                      background: 'rgba(124,58,237,0.1)',
                      color: '#7C3AED',
                      fontWeight: 700,
                      mb: 2.5,
                      fontSize: '0.72rem',
                    }}
                  />
                  <FeatureIcon
                    icon={ReceiptLongIcon}
                    color="#7C3AED"
                    bg="rgba(124,58,237,0.1)"
                  />
                  <Typography variant="h3" sx={{ mb: 2, fontSize: { xs: '1.4rem', md: '1.7rem' } }}>
                    Track Every Group Expense — Automatically
                  </Typography>
                  <Typography sx={{ color: '#5C4F3D', lineHeight: 1.75, fontSize: '1rem' }}>
                    Splitting costs for a party, trip, or gift? ListLink's expense dashboard
                    tracks what you've spent per person and event. No spreadsheets.
                    No awkward conversations.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <MiniExpenseDashboard />
                </Grid>
              </Grid>
            </Paper>
          </AnimatedSection>

          {/* Features 2–4: Three-column grid */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {[
              {
                icon: VisibilityOffIcon,
                color: '#E11D48',
                bg: 'rgba(225,29,72,0.1)',
                title: 'Secret Purchases, Without the Spreadsheet',
                body: 'Mark any item as secretly buying. Other participants see it\'s taken — but the list owner never sees it was purchased, keeping the surprise intact.',
              },
              {
                icon: ListAltIcon,
                color: '#E8891A',
                bg: 'rgba(232,137,26,0.1)',
                title: 'Gift Lists AND Grocery Lists — One App',
                body: 'Switch between your Christmas wish list and your weekly grocery run without switching apps. One place, all your lists.',
              },
              {
                icon: CelebrationIcon,
                color: '#EA580C',
                bg: 'rgba(234,88,12,0.1)',
                title: 'Built for Sinterklaas 🇳🇱🇧🇪',
                body: 'The only list app with dedicated Sinterklaas event support. Organize Surprise exchanges, manage wish lists, and celebrate Dutch tradition — digitally.',
                badge: 'For NL & BE',
              },
            ].map(({ icon, color, bg, title, body, badge }) => (
              <Grid item xs={12} sm={6} md={4} key={title}>
                <AnimatedSection delay={0.1}>
                  <Paper
                    sx={{
                      p: 3.5,
                      height: '100%',
                      borderRadius: '20px',
                      border: '1px solid #EDE8DF',
                      boxShadow: '0 4px 24px rgba(26,20,16,0.07)',
                      cursor: 'default',
                    }}
                  >
                    {badge && (
                      <Chip
                        label={badge}
                        size="small"
                        sx={{
                          background: 'rgba(234,88,12,0.1)',
                          color: '#EA580C',
                          fontWeight: 700,
                          mb: 2,
                          fontSize: '0.68rem',
                        }}
                      />
                    )}
                    <FeatureIcon icon={icon} color={color} bg={bg} />
                    <Typography variant="h5" sx={{ mb: 1.5, fontSize: '1.05rem' }}>
                      {title}
                    </Typography>
                    <Typography sx={{ color: '#5C4F3D', lineHeight: 1.7, fontSize: '0.9rem' }}>
                      {body}
                    </Typography>
                  </Paper>
                </AnimatedSection>
              </Grid>
            ))}
          </Grid>

          {/* Feature 5: Full-width — Sharing */}
          <AnimatedSection delay={0.1}>
            <Paper
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: '24px',
                border: '1px solid #EDE8DF',
                boxShadow: '0 4px 24px rgba(26,20,16,0.07)',
                '&:hover': { transform: 'none' },
              }}
            >
              <Grid container spacing={5} alignItems="center">
                <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', order: { xs: 2, md: 1 } }}>
                  {/* Share illustration */}
                  <Box
                    sx={{
                      background: 'rgba(14,165,233,0.06)',
                      borderRadius: 3,
                      p: 4,
                      maxWidth: 300,
                      width: '100%',
                      border: '1px solid rgba(14,165,233,0.15)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          background: 'rgba(14,165,233,0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <ShareIcon sx={{ color: '#0EA5E9', fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#1A1410', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                          Emma's Gift List
                        </Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: '#5C4F3D', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                          8 items · Shared link
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        background: '#F7F2EA',
                        borderRadius: 2,
                        px: 2,
                        py: 1.25,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1.5,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.7rem', color: '#8A7968', fontFamily: '"Plus Jakarta Sans", sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 1 }}>
                        list-link.com/l/emma-xmas
                      </Typography>
                      <Chip label="Copy" size="small" sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, background: '#0EA5E9', color: '#fff', cursor: 'pointer' }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.7rem', color: '#5C4F3D', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                      ✓ Share with anyone who has an account
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
                  <FeatureIcon icon={ShareIcon} color="#0EA5E9" bg="rgba(14,165,233,0.1)" />
                  <Typography variant="h3" sx={{ mb: 2, fontSize: { xs: '1.4rem', md: '1.7rem' } }}>
                    Share With Your Group — Instantly
                  </Typography>
                  <Typography sx={{ color: '#5C4F3D', lineHeight: 1.75, fontSize: '1rem' }}>
                    Invite friends and family by email. Everyone gets access to the shared lists and can mark items as purchased — without spoiling the surprise for the list owner.
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </AnimatedSection>
        </Container>
      </Box>

      {/* ── SECTION 3: HOW IT WORKS ──────────────────────────────────────────── */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          background: 'linear-gradient(180deg, #1A1410 0%, #2D1F14 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Ambient glow */}
        <Box
          sx={{
            position: 'absolute',
            width: 600,
            height: 600,
            borderRadius: '50%',
            background: 'radial-gradient(ellipse, rgba(232,137,26,0.08) 0%, transparent 70%)',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <AnimatedSection>
            <Typography
              variant="overline"
              sx={{ color: '#E8891A', display: 'block', mb: 1, textAlign: 'center' }}
            >
              How It Works
            </Typography>
            <Typography
              variant="h2"
              sx={{
                textAlign: 'center',
                mb: 8,
                color: '#FDFAF5',
                fontSize: { xs: '1.9rem', md: '2.5rem' },
              }}
            >
              Up and Running in 60 Seconds
            </Typography>
          </AnimatedSection>

          <Grid container spacing={3}>
            {[
              {
                num: '01',
                title: 'Create Your List or Event',
                body: 'Pick from gift lists, wish lists, Secret Santa, Sinterklaas, or general shared lists. Takes 30 seconds.',
              },
              {
                num: '02',
                title: 'Invite Your People',
                body: 'Invite by email. Collaborators join instantly and get access to all the shared lists in the event.',
              },
              {
                num: '03',
                title: 'Shop, Track, Celebrate',
                body: 'Mark purchases secretly, track expenses, see who\'s buying what. No surprises are spoiled.',
              },
            ].map(({ num, title, body }, i) => (
              <Grid item xs={12} md={4} key={num}>
                <AnimatedSection delay={i * 0.1}>
                  <Box
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: 3,
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.09)',
                      backdropFilter: 'blur(8px)',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Fraunces", serif',
                        fontWeight: 900,
                        fontSize: '4rem',
                        lineHeight: 1,
                        mb: 3,
                        background: 'linear-gradient(135deg, #E8891A 0%, #F5A94E 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      {num}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{ color: '#FDFAF5', mb: 1.5, fontSize: '1.05rem' }}
                    >
                      {title}
                    </Typography>
                    <Typography
                      sx={{ color: 'rgba(253,250,245,0.6)', lineHeight: 1.7, fontSize: '0.9rem' }}
                    >
                      {body}
                    </Typography>
                  </Box>
                </AnimatedSection>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── SECTION 4: COMPARISON ────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 10, md: 14 }, background: '#FDFAF5' }}>
        <Container maxWidth="md">
          <AnimatedSection>
            <Typography
              variant="overline"
              sx={{ color: '#E8891A', display: 'block', mb: 1, textAlign: 'center' }}
            >
              Why ListLink
            </Typography>
            <Typography
              variant="h2"
              sx={{
                textAlign: 'center',
                mb: 2,
                fontSize: { xs: '1.9rem', md: '2.4rem' },
              }}
            >
              Built Different. Actually Free.
            </Typography>
            <Typography
              sx={{
                textAlign: 'center',
                color: '#5C4F3D',
                mb: 6,
                maxWidth: 420,
                mx: 'auto',
              }}
            >
              Compare ListLink to the alternatives — there's nothing else like it.
            </Typography>

            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 3,
                border: '1px solid #EDE8DF',
                boxShadow: '0 4px 24px rgba(26,20,16,0.07)',
                '&:hover': { transform: 'none' },
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1A1410', py: 2.5 }}>
                      Feature
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 800,
                        fontSize: '0.9rem',
                        color: '#E8891A',
                        background: 'rgba(232,137,26,0.05)',
                        py: 2.5,
                      }}
                    >
                      ListLink
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#8A7968', py: 2.5 }}>
                      Elfster
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#8A7968', py: 2.5 }}>
                      Giftster
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparisonRows.map(({ feature, listlink, elfster, giftster }, i) => (
                    <TableRow
                      key={feature}
                      sx={{
                        background: i % 2 === 0 ? '#FFFFFF' : '#FDFAF5',
                        '&:last-child td': { border: 0 },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', color: '#1A1410', py: 2.25 }}>
                        {feature}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ background: 'rgba(232,137,26,0.03)', py: 2.25 }}
                      >
                        <ComparisonCell value={listlink} />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2.25 }}>
                        <ComparisonCell value={elfster} />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2.25 }}>
                        <ComparisonCell value={giftster} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AnimatedSection>
        </Container>
      </Box>

      {/* ── SECTION 5: FINAL CTA ─────────────────────────────────────────────── */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          background: 'linear-gradient(135deg, #E8891A 0%, #C46F0C 100%)',
          position: 'relative',
          overflow: 'hidden',
          textAlign: 'center',
        }}
      >
        {/* Decorative radial glows */}
        <Box sx={{ position: 'absolute', top: -100, left: -100, width: 400, height: 400, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -100, right: -100, width: 400, height: 400, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} />

        <Container maxWidth="sm" sx={{ position: 'relative' }}>
          <AnimatedSection>
            <Typography
              variant="h2"
              sx={{
                color: '#FFFFFF',
                mb: 2,
                fontSize: { xs: '2.2rem', md: '3rem' },
                fontStyle: 'italic',
              }}
            >
              Your List Is Waiting.
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: '1.05rem',
                mb: 5,
                lineHeight: 1.6,
              }}
            >
              Free. No credit card. Ready in 60 seconds.
            </Typography>
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/register"
              sx={{
                background: '#FFFFFF',
                color: '#E8891A',
                fontWeight: 800,
                fontSize: '1rem',
                px: 5,
                py: 1.75,
                boxShadow: '0 8px 32px rgba(26,20,16,0.2)',
                '&:hover': {
                  background: '#FDFAF5',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(26,20,16,0.25)',
                },
              }}
            >
              Get Started Free →
            </Button>
          </AnimatedSection>
        </Container>
      </Box>
    </Box>
  );
}

export default HomePage;
